import { useEffect, useEffectEvent } from "react";
import { API_BASE } from "../api/client";
import type { LiveMessage } from "../api/types";

/** Close codes of the server's live channel (server/src/modules/live/live.ts). */
export const LIVE_CLOSE = {
  notPaired: 4401,
  signedOut: 4402,
  shopInactive: 4403,
  termsRequired: 4423,
  replaced: 4409,
} as const;

const RECONNECT_DELAYS_MS = [1_000, 2_000, 5_000, 10_000];

export interface LiveHandlers {
  onMessage: (message: LiveMessage) => void;
  /** The server refused the connection for good (unpaired, paused shop, terms). */
  onRefused: (code: number) => void;
  onConnectionChange?: (connected: boolean) => void;
}

/**
 * One WebSocket to the server while the till is unlocked. After a lost connection it reconnects by
 * itself and asks the screens to reload (`resync`), so no update is ever missed. A waiter's or courier's
 * phone also gives who signed in on it (`staffToken`). After a restart of the server all devices come
 * back at once; a random wait spreads them out.
 */
export function useLive(deviceToken: string, handlers: LiveHandlers, staffToken?: string | null): void {
  const onMessage = useEffectEvent(handlers.onMessage);
  const onRefused = useEffectEvent(handlers.onRefused);
  const onConnectionChange = useEffectEvent((connected: boolean) => handlers.onConnectionChange?.(connected));

  useEffect(() => {
    let socket: WebSocket | null = null;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let attempt = 0;
    let stopped = false;

    const connect = () => {
      const protocol = window.location.protocol === "https:" ? "wss" : "ws";
      const staff = staffToken ? `&staff=${encodeURIComponent(staffToken)}` : "";
      socket = new WebSocket(
        `${protocol}://${window.location.host}${API_BASE}/live?token=${encodeURIComponent(deviceToken)}${staff}`,
      );
      socket.onmessage = (event) => {
        if (typeof event.data !== "string") return;
        try {
          const message = JSON.parse(event.data) as LiveMessage;
          if (message.type === "hello") {
            attempt = 0;
            onConnectionChange(true);
            // anything may have changed while we were away, or between the first load and this
            // connection (a sale on another device at that moment would otherwise be missed)
            onMessage({ type: "resync" });
            return;
          }
          onMessage(message);
        } catch {
          onMessage({ type: "resync" });
        }
      };
      socket.onclose = (event) => {
        if (stopped) return;
        const refused: readonly number[] = [
          LIVE_CLOSE.notPaired,
          LIVE_CLOSE.signedOut,
          LIVE_CLOSE.shopInactive,
          LIVE_CLOSE.termsRequired,
        ];
        if (refused.includes(event.code)) {
          onRefused(event.code);
          return;
        }
        onConnectionChange(false);
        const delay = RECONNECT_DELAYS_MS[Math.min(attempt, RECONNECT_DELAYS_MS.length - 1)]! * (0.5 + Math.random());
        attempt++;
        timer = setTimeout(connect, delay);
      };
    };

    connect();
    return () => {
      stopped = true;
      clearTimeout(timer);
      socket?.close();
    };
  }, [deviceToken, staffToken]);
}
